import React from "react";
import { useParams } from "react-router-dom";

const blogPosts = {
    "veilig-wonen": {
        title: "Veilig wonen: Zo bescherm je jouw huis tegen inbraak",
        category: "Woningbeveiliging",
        date: "21 april 2025",
        imageSrc: "https://images.unsplash.com/photo-1557597774-9d273605dfa9?q=80&w=1726&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        content: `
            <p>Een veilig huis is de basis voor een prettig leven. Niemand wil zich zorgen maken over inbraak of een onveilig gevoel in zijn eigen woning. Toch zijn veel huizen in Nederland onnodig kwetsbaar. In dit artikel ontdek je hoe je jouw woning in een paar stappen aanzienlijk beter kunt beveiligen — zonder dat het je duizenden euro’s hoeft te kosten.</p>
        
            <h2>Waarom woningbeveiliging belangrijk is</h2>
            <p>Inbrekers hebben vaak slechts 30 seconden nodig om binnen te komen. Vooral bij woningen zonder modern hang- en sluitwerk of buitenverlichting is het voor hen letterlijk kinderspel. Een inbraak is niet alleen financieel vervelend, maar heeft ook grote psychologische impact: het idee dat iemand in je persoonlijke ruimte is geweest, laat sporen na. Gelukkig kun je met relatief simpele aanpassingen al enorm veel voorkomen.</p>
        
            <h2>1. Begin met het hang- en sluitwerk</h2>
            <p>Controleer of je deuren en ramen voorzien zijn van hang- en sluitwerk met minimaal <strong>SKG**2 sterren</strong>. Voor buitendeuren adviseren we altijd kerntrekbeveiliging, omdat kerntrekken een van de meestgebruikte inbraakmethodes is.</p>
            <p>Laat vooral oude sloten (ouder dan 10 jaar) vervangen. Nieuwe sloten zijn vaak getest op moderne inbraakmethodes en voldoen aan het Politiekeurmerk Veilig Wonen (PKVW).</p>
        
            <h2>2. Buitenverlichting met bewegingssensor</h2>
            <p>Verlichting rondom je woning schrikt inbrekers direct af. Plaats lampen met bewegingssensor bij de voordeur, achterdeur, oprit en donkere hoeken. Slimme verlichting met timerfunctie helpt ook bij het simuleren van aanwezigheid als je op vakantie bent.</p>
        
            <h2>3. Slimme trucs om aanwezigheid te faken</h2>
            <ul>
              <li>Plaats tijdschakelaars op lampen in de woonkamer en slaapkamer.</li>
              <li>Laat een kopje of krant zichtbaar achter op tafel.</li>
              <li>Laat de post niet opstapelen — vraag buren om dit weg te halen.</li>
            </ul>
            <p>Het idee is dat het huis 'leeft'. Inbrekers kijken vaak eerst een paar dagen naar hun doelwit voordat ze toeslaan.</p>
        
            <h2>4. Schakel de buren in</h2>
            <p>Sociale controle is één van de beste beveiligingsmethoden. Spreek af met je buren dat jullie een oogje in het zeil houden tijdens afwezigheid. Een buurt-WhatsAppgroep is ook effectief om verdachte situaties snel te signaleren.</p>
        
            <h2>5. Beveilig ook je ramen</h2>
            <p>Veel mensen richten zich vooral op de voordeur, maar inbrekers komen juist vaak via ramen naar binnen — vooral op de begane grond of via een plat dak. Beveilig deze met bijzetsloten en raamsloten. Overweeg ook barrièrestangen of veiligheidsglas op risicovolle plekken.</p>
        
            <h2>6. Camera’s en alarmsystemen</h2>
            <p>Een zichtbaar alarmsysteem of een dummy-camera werkt preventief. Voor maximale controle zijn slimme systemen met meldingen op je smartphone het meest effectief. Je ziet dan direct als er beweging is, waar je ook bent.</p>
        
            <h2>Wat kost goede woningbeveiliging?</h2>
            <p>Veel mensen denken dat beveiliging duizenden euro’s kost, maar dat is niet zo. Een basisinstallatie met cilindersloten, kerntrekbeslag, verlichting en een alarmsysteem kan al onder de €500 gerealiseerd worden. De schade van een inbraak is vaak vele malen duurder.</p>
        
            <h2>Wil je persoonlijk advies?</h2>
            <p>Ieder huis is anders, en dus ook de beste aanpak. Onze monteurs geven vrijblijvend advies op locatie. Samen bekijken we waar jouw woning kwetsbaar is en welke oplossingen het beste passen bij jouw situatie en budget.</p>
        
            <p><strong>Wil je snel geholpen worden?</strong> Wij zijn 24/7 bereikbaar. <a href="/offerte-aanvragen"><strong class="italic text-blue-500">Vraag hier gratis en vrijblijvend een offerte aan</strong> of bel direct met onze specialisten. Zo ben je snel én professioneel geholpen.</p>
            <section class="py-20 mt-10 bg-gray-100">
              <div class="max-w-screen-lg mx-auto px-6 text-center">
                <h2 class="text-3xl font-bold mb-4">Alles over woningbeveiliging in één brochure</h2>
                <p class="text-gray-600 mb-6">Ontdek hoe je jouw woning beveiligt zonder hak- of breekwerk. Alle oplossingen overzichtelijk in één document.</p>
                <a href="/brochure.pdf" target="_blank" class="px-6 py-3 bg-primary-500 text-black font-semibold rounded-lg shadow hover:bg-primary-700">
                  📄 Gratis brochure downloaden
                </a>
              </div>
            </section>  
        `
    },
    "kerntrekbeslag": {
        title: "Waarom kerntrekbeslag onmisbaar is voor je voordeur",
        category: "Kerntrekbeveiliging",
        date: "1 juni 2025",
        imageSrc: "https://images.unsplash.com/photo-1614424428282-b2b1e72c6a4e?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        content: `
            <p>Wist je dat kerntrekken nog altijd een van de meest gebruikte inbraakmethodes in Nederland is? In minder dan 30 seconden kan een inbreker je cilinderslot forceren — tenzij je woning voorzien is van goed kerntrekbeslag.</p>
        
            <h2>Wat is kerntrekken precies?</h2>
            <p>Kerntrekken (ook wel 'cilindertrekken' genoemd) is een inbraaktechniek waarbij een geharde schroef in de cilinder van je slot wordt gedraaid. Met een speciale hamer of trekker wordt de hele cilinder vervolgens uit het beslag getrokken. De deur gaat daarna met een simpele schroevendraaier open.</p>
        
            <h2>Hoe werkt kerntrekbeslag?</h2>
            <p>Kerntrekbeslag is speciaal ontworpen om deze methode onmogelijk te maken. Het bestaat uit een stalen plaat die de cilinder volledig bedekt en versterkt. Hierdoor kan een inbreker niet meer bij de kern van het slot komen.</p>
        
            <h2>Voordelen van kerntrekbeslag</h2>
            <ul>
              <li><strong>Optimale bescherming:</strong> voorkomt 90% van de kerntrekpogingen.</li>
              <li><strong>SKG-keurmerk:</strong> beslag met 3 sterren voldoet aan de hoogste norm.</li>
              <li><strong>Langdurige investering:</strong> roestvast staal en onderhoudsarm.</li>
              <li><strong>Geschikt voor alle deuren:</strong> ook voor achterdeuren en schuurdeuren.</li>
            </ul>
        
            <h2>Is kerntrekbeveiliging verplicht?</h2>
            <p>Bij nieuwbouwwoningen en bij het Politiekeurmerk Veilig Wonen is kerntrekbeveiliging verplicht. Maar ook bij bestaande woningen wordt het sterk aangeraden — zeker in steden of buitenwijken waar inbraakrisico’s hoger zijn.</p>
        
            <h2>Laat je beslag professioneel installeren</h2>
            <p>Kerntrekbeslag werkt alleen goed als het correct is gemonteerd. Onze ervaren monteurs zorgen voor een nette afwerking en correcte plaatsing, zonder schade aan je deur of kozijn.</p>
        
            <h2>Prijs en installatie</h2>
            <p>Een gemiddeld setje kerntrekbeslag kost tussen de €40 en €90, afhankelijk van het merk en de afwerking. Inclusief montage ben je vaak voor minder dan €150 klaar — een kleine prijs voor gemoedsrust en inbraakpreventie.</p>
        
            <p><strong>Wil je weten welk beslag geschikt is voor jouw woning?</strong> Neem vandaag nog contact met ons op voor gratis advies of <a href="/offerte-aanvragen"><strong class="italic text-blue-500">vraag direct een vrijblijvende offerte aan</strong></a>.</p>
            <section class="py-20 bg-gray-100">
              <div class="max-w-screen-lg mx-auto px-6 text-center">
                <h2 class="text-3xl font-bold mb-4">Alles over woningbeveiliging in één brochure</h2>
                <p class="text-gray-600 mb-6">Ontdek hoe je jouw woning beveiligt zonder hak- of breekwerk. Alle oplossingen overzichtelijk in één document.</p>
                <a href="/brochure.pdf" target="_blank" class="px-6 py-3 bg-primary-500 text-black font-semibold rounded-lg shadow hover:bg-primary-700">
                  📄 Gratis brochure downloaden
                </a>
              </div>
            </section>            
        `
    },
    "meerpuntssluitingen": {
        title: "Meerpuntssluitingen: extra veiligheid met één draai",
        category: "Veilige sloten",
        date: "12 mei 2025",
        imageSrc: "https://images.unsplash.com/photo-1578912084730-23a3182cdf27?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        content: `
            <p>Als je écht veilig wilt wonen, is een standaard cilinderslot niet altijd genoeg. Inbrekers weten precies waar ze kracht moeten zetten om je deur open te breken. Meerpuntssluitingen verdelen deze druk over meerdere punten — en maken zo het verschil tussen een kwetsbare en een beveiligde woning.</p>
        
            <h2>Wat is een meerpuntssluiting?</h2>
            <p>Een meerpuntssluiting is een slotmechanisme dat op meerdere punten tegelijk in het kozijn vergrendelt. Met slechts één draaibeweging van de sleutel (of deurklink) vergrendel je bovenaan, in het midden én onderaan je deur.</p>
        
            <h2>Voordelen van meerpuntssluitingen</h2>
            <ul>
              <li><strong>Hogere inbraakwerendheid:</strong> verdeelt de kracht over meerdere sluitpunten.</li>
              <li><strong>Strakke sluiting:</strong> voorkomt kromtrekken van deuren en tocht.</li>
              <li><strong>Gebruiksgemak:</strong> één handeling voor complete afsluiting.</li>
              <li><strong>Toepasbaar op ramen en schuifpuien:</strong> voor 360° beveiliging van je woning.</li>
            </ul>
        
            <h2>Soorten meerpuntssluitingen</h2>
            <p>Er zijn verschillende types: met haakschoten, pennen, of rolsloten. Afhankelijk van je type deur en gebruiksdoel adviseren wij welke variant het beste past. Ook zijn er varianten met krukbediening of sleutelbediening mogelijk.</p>
        
            <h2>Is jouw deur geschikt?</h2>
            <p>De meeste houten en kunststof deuren zijn geschikt voor montage. Zelfs bij bestaande deuren zonder sluitingen kunnen we vrijwel altijd een passende oplossing realiseren.</p>
        
            <h2>Installatie en kosten</h2>
            <p>De montage van een meerpuntssluiting vergt precisie. Onze slotenmakers meten, frezen en installeren zonder schade aan je deur of kozijn. De kosten liggen gemiddeld tussen €250 en €400, afhankelijk van type sluiting en deur.</p>
        
            <h2>Conclusie: één simpele draai, maximale zekerheid</h2>
            <p>Met een meerpuntssluiting maak je het inbrekers een stuk moeilijker én verhoog je je wooncomfort. Combineer dit met kerntrekbeslag voor een complete beveiligingsoplossing.</p>
        
            <p><strong>Benieuwd naar de mogelijkheden voor jouw woning?</strong> <a href="/offerte-aanvragen"><strong class="italic text-blue-500">Vraag vrijblijvend een offerte aan</strong></a> of neem direct contact met ons op voor persoonlijk advies.</p>
            <section class="py-20 bg-gray-100">
              <div class="max-w-screen-lg mx-auto px-6 text-center">
                <h2 class="text-3xl font-bold mb-4">Alles over woningbeveiliging in één brochure</h2>
                <p class="text-gray-600 mb-6">Ontdek hoe je jouw woning beveiligt zonder hak- of breekwerk. Alle oplossingen overzichtelijk in één document.</p>
                <a href="/brochure.pdf" target="_blank" class="px-6 py-3 bg-primary-500 text-black font-semibold rounded-lg shadow hover:bg-primary-700">
                  📄 Gratis brochure downloaden
                </a>
              </div>
            </section>
        `
    },
    "vca-certificering": {
        title: "VCA gecertificeerd: veiligheid en professionaliteit gegarandeerd",
        category: "Certificering",
        date: "september 10 2025",
        imageSrc: "/logo-vca.png",
        content: `
        <p>Als het gaat om veiligheid op de werkvloer en bij werkzaamheden bij klanten, is vertrouwen essentieel. Met een VCA-certificering laat Keyservice 24/7 zien dat wij werken volgens de hoogste normen op het gebied van veiligheid, gezondheid en milieu. Voor u betekent dit: betrouwbare service zonder compromissen.</p>
    
        <h2>Wat is VCA-certificering?</h2>
        <p>VCA staat voor <em>Veiligheid, Gezondheid en Milieu (VGM) Checklist Aannemers</em>. Het is een veelgebruikt certificeringssysteem in Nederland en Europa dat aantoont dat een bedrijf veilig en professioneel werkt. Voor klanten geeft dit extra zekerheid dat alle werkzaamheden volgens strikte richtlijnen worden uitgevoerd.</p>
    
        <h2>Voordelen van een VCA-gecertificeerd bedrijf</h2>
        <ul>
          <li><strong>Veiligheid voorop:</strong> onze monteurs werken volgens duidelijke veiligheidsprotocollen.</li>
          <li><strong>Professioneel personeel:</strong> al onze medewerkers zijn getraind en bewust van risico’s.</li>
          <li><strong>Betrouwbare uitvoering:</strong> van klein hang- en sluitwerk tot grote sluitplannen, altijd volgens de regels.</li>
          <li><strong>Geschikt voor zakelijke klanten:</strong> veel bedrijven en instellingen eisen VCA als standaard.</li>
        </ul>
    
        <h2>Waarom kiezen voor Keyservice 24/7?</h2>
        <p>Onze VCA-certificering bewijst dat wij veiligheid en kwaliteit structureel borgen in ons werk. Of het nu gaat om spoedservice bij buitensluiting, installatie van meerpuntssluitingen of complete beveiligingsprojecten: u kunt vertrouwen op onze deskundigheid.</p>
    
        <h2>Wat betekent dit voor u als klant?</h2>
        <p>Met een VCA-gecertificeerd bedrijf weet u dat de werkzaamheden worden uitgevoerd met oog voor veiligheid en zonder onnodige risico’s. Dit geeft niet alleen gemoedsrust, maar vaak ook voordelen bij verzekeringen en zakelijke audits.</p>
    
        <h2>Conclusie: zekerheid zwart op wit</h2>
        <p>Met de VCA-certificering biedt Keyservice 24/7 niet alleen vakmanschap, maar ook de garantie dat dit veilig, verantwoord en professioneel gebeurt.</p>
    
        <p><strong>Wilt u meer weten over wat onze VCA-certificering voor uw bedrijf betekent?</strong> <a href="/contact"><strong class="italic text-blue-500">Neem contact met ons op</strong></a> of <a href="/offerte-aanvragen"><strong class="italic text-blue-500">vraag vrijblijvend een offerte aan</strong></a>.</p>
    
        <section class="py-20 bg-gray-100">
          <div class="max-w-screen-lg mx-auto px-6 text-center">
            <h2 class="text-3xl font-bold mb-4">Meer weten over onze aanpak?</h2>
            <p class="text-gray-600 mb-6">Download onze brochure en ontdek hoe Keyservice 24/7 veiligheid en kwaliteit combineert in al onze diensten.</p>
            <a href="/brochure.pdf" target="_blank" class="px-6 py-3 bg-primary-500 text-black font-semibold rounded-lg shadow hover:bg-primary-700">
              📄 Gratis brochure downloaden
            </a>
          </div>
        </section>
      `
    }
};

export default function BlogPostPage() {
    const { slug } = useParams();
    const post = blogPosts[slug];

    if (!post) {
        return (
            <div className="text-center py-32">
                <h1 className="text-3xl font-bold">Blogpost niet gevonden</h1>
            </div>
        );
    }

    return (
        <div className="max-w-screen-lg mx-auto px-6 py-20">
            <p className="uppercase text-sm text-primary-500 mb-2">{post.category}</p>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
            <p className="text-sm text-gray-500 mb-6">{post.date}</p>
            <img
                src={post.imageSrc}
                alt={post.title}
                className="rounded-lg mb-8 w-full object-cover"
            />
            <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: post.content }} />
        </div>
    );
}
